import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CalendarClock, ChevronLeft, ChevronRight, History, Monitor, Search, Send, Smartphone, UserPlus, X } from "lucide-react";
import { Button } from "components/ui/Button";
import { Input } from "components/ui/Input";
import { Badge } from "components/ui/Badge";
import {
    EmailingStatus,
    MailTemplate,
    Preview,
    RecipientRef,
    SentHistory,
    Skipped,
    checkRecipients,
    createEmailingContact,
    getSentHistory,
    previewMail,
    recipientKey,
    searchRecipients,
    sendMail,
    sendTestMail,
} from "interfaces/emailing";
import { CategoryBadge, MailFrame, Modal, Segmented, UnpublishedNotice, fieldLabel, formatDate, parisInputValue, textareaClass } from "./parts";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SKIP_REASON: Record<Skipped["reason"], string> = {
    no_email: "pas d'adresse email",
    opted_out: "désinscrit des emails marketing",
    not_found: "introuvable",
    recently_sent: "déjà reçu récemment",
};

const skipKey = (s: Skipped) => recipientKey({ kind: s.kind, _id: s.id });

/** Step header: number chip + title, so the three steps read top to bottom. */
function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
    return (
        <section className="space-y-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink text-xs text-ink-inverse">{n}</span>
                {title}
            </h3>
            {children}
        </section>
    );
}

/**
 * Users and prospects in one field. A typed address nobody has yet can be added on the spot as a
 * prospect; if it turns out to have an account, the user is added instead.
 */
function RecipientSearch({ onAdd, exclude }: { onAdd: (r: RecipientRef) => void; exclude: Set<string> }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState<(RecipientRef & { company?: string })[]>([]);
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setError(null);
        if (q.trim().length < 2) return setResults([]);
        const t = setTimeout(() => {
            searchRecipients(q.trim())
                .then(setResults)
                .catch(() => setResults([]));
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    const typed = q.trim().toLowerCase();
    const shown = results.filter((r) => !exclude.has(recipientKey(r)));
    const canAddNew = EMAIL_RX.test(typed) && !results.some((r) => r.email?.toLowerCase() === typed);

    const pick = (r: RecipientRef) => {
        onAdd(r);
        setQ("");
    };

    const addNew = async () => {
        setAdding(true);
        const res = await createEmailingContact({ email: typed });
        setAdding(false);
        if (res.user) return pick(res.user);
        if (res.contact) return pick({ kind: "contact", _id: res.contact._id, email: res.contact.email, name: res.contact.name });
        setError(res.error || "Ajout impossible");
    };

    return (
        <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-ink-faint" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, email ou entreprise — ou une nouvelle adresse" className="pl-9" />
            {(shown.length > 0 || canAddNew) && (
                <div className="absolute inset-x-0 top-full z-10 mt-1 max-h-72 overflow-y-auto rounded-2xl border border-border bg-surface p-2 shadow-float">
                    {shown.map((r) => (
                        <button
                            key={recipientKey(r)}
                            type="button"
                            onClick={() => pick(r)}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-surface-sunken"
                        >
                            <span className="min-w-0 flex-1 truncate">
                                {r.name || r.email}
                                {r.name && <span className="text-ink-muted"> · {r.email}</span>}
                                {r.company && <span className="text-ink-muted"> · {r.company}</span>}
                            </span>
                            <span className="shrink-0 text-xs text-ink-faint">{r.kind === "user" ? "Utilisateur" : "Prospect"}</span>
                        </button>
                    ))}
                    {canAddNew && (
                        <button
                            type="button"
                            onClick={addNew}
                            disabled={adding}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-surface-sunken disabled:opacity-40"
                        >
                            <UserPlus className="h-4 w-4 shrink-0 text-ink-faint" />
                            <span className="truncate">
                                {adding ? "Ajout…" : "Écrire à"} <strong>{typed}</strong> <span className="text-ink-muted">(nouveau prospect)</span>
                            </span>
                        </button>
                    )}
                </div>
            )}
            {error && <p className="mt-1 text-xs text-danger">{error}</p>}
        </div>
    );
}

const DAY = 86400000;

/**
 * When to send: now, or a day and an hour in French time — the inputs hold plain Paris wall-clock
 * strings, never the browser's own zone, and the backend reads them as Europe/Paris.
 */
function When({ date, time, onDate, onTime, maxDays }: { date: string; time: string; onDate: (v: string) => void; onTime: (v: string) => void; maxDays: number }) {
    const now = Date.now();
    const at = new Date(`${date}T${time}`); // only used for the weekday label below
    return (
        <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className={fieldLabel}>Jour</label>
                    <Input type="date" value={date} min={parisInputValue(new Date(now)).slice(0, 10)} max={parisInputValue(new Date(now + maxDays * DAY)).slice(0, 10)} onChange={(e) => onDate(e.target.value)} />
                </div>
                <div>
                    <label className={fieldLabel}>Heure (Paris)</label>
                    <Input type="time" value={time} step={300} onChange={(e) => onTime(e.target.value)} />
                </div>
            </div>
            {date && time && !isNaN(at.getTime()) && (
                <p className="flex items-center gap-1 text-xs text-ink-muted">
                    <CalendarClock className="h-3 w-3 shrink-0" />
                    Départ le {at.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} à {time.replace(":", "h")}, heure de Paris · {maxDays} jours à l'avance au plus
                </p>
            )}
        </div>
    );
}

/** « Déjà envoyé » and « Programmé » lines under a template, from our own send history. */
function AlreadySent({ rows, total }: { rows?: SentHistory[string]; total: number }) {
    if (!rows?.length) return null;
    const sent = rows.filter((r) => r.count > 0);
    const planned = rows.filter((r) => r.scheduledCount > 0);
    const last = sent.reduce<string | undefined>((m, r) => (r.lastSentAt && (!m || r.lastSentAt > m) ? r.lastSentAt : m), undefined);
    const next = planned.reduce<string | undefined>((m, r) => (r.nextScheduledAt && (!m || r.nextScheduledAt < m) ? r.nextScheduledAt : m), undefined);
    const times = sent.reduce((n, r) => n + r.count, 0);
    return (
        <>
            {sent.length > 0 && (
                <p className="mt-1 flex items-center gap-1 text-xs text-warning">
                    <History className="h-3 w-3 shrink-0" />
                    {total === 1
                        ? `Déjà envoyé${times > 1 ? ` ${times} fois` : ""}, le dernier le ${formatDate(last, true)}`
                        : `Déjà reçu par ${sent.length}/${total} · dernier envoi le ${formatDate(last)}`}
                </p>
            )}
            {planned.length > 0 && (
                <p className="mt-1 flex items-center gap-1 text-xs text-warning">
                    <CalendarClock className="h-3 w-3 shrink-0" />
                    {total === 1
                        ? `Programmé pour le ${formatDate(next, true)} (heure de Paris)`
                        : `Programmé pour ${planned.length}/${total} · prochain départ le ${formatDate(next, true)}`}
                </p>
            )}
        </>
    );
}

export default function Composer({
    status,
    templates,
    initialRecipients,
    initialTemplateId,
    onClose,
    onSent,
}: {
    status: EmailingStatus | null;
    templates: MailTemplate[];
    initialRecipients: RecipientRef[];
    initialTemplateId?: string;
    onClose: () => void;
    onSent: () => void;
}) {
    const [recipients, setRecipients] = useState<RecipientRef[]>(initialRecipients);
    const [templateId, setTemplateId] = useState(initialTemplateId ?? "");
    const [variables, setVariables] = useState<Record<string, string>>({});
    const [replyTo, setReplyTo] = useState("");
    const [previewIdx, setPreviewIdx] = useState(0);
    const [mobile, setMobile] = useState(false);
    const [preview, setPreview] = useState<Preview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [history, setHistory] = useState<SentHistory>({});
    const [check, setCheck] = useState<{ sendable: number; skipped: Skipped[]; repeats: Skipped[] } | null>(null);
    const [confirmRepeats, setConfirmRepeats] = useState<Skipped[] | null>(null);
    const [sending, setSending] = useState(false);
    const [notice, setNotice] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
    const [result, setResult] = useState<{ sent: number; failed: number; skipped: Skipped[]; scheduledFor?: string } | null>(null);
    // Programmed send: tomorrow 9h, Paris time, by default.
    const [later, setLater] = useState(false);
    const [date, setDate] = useState(() => parisInputValue(new Date(Date.now() + DAY)).slice(0, 10));
    const [time, setTime] = useState("09:00");
    const previewReq = useRef(0);

    const template = templates.find((t) => t._id === templateId);
    // A Resend template sends from the address set in Resend.
    const from = template?.resend?.from || status?.resend.from;
    const keys = useMemo(() => new Set(recipients.map(recipientKey)), [recipients]);
    const previewRecipient = recipients[Math.min(previewIdx, recipients.length - 1)];
    const missing = (template?.freeVariables ?? []).filter((k) => !variables[k]?.trim());

    // Preview: the real render, for the recipient being looked at (only the latest request may land).
    useEffect(() => {
        if (!templateId) return setPreview(null);
        const req = ++previewReq.current;
        setPreviewLoading(true);
        const t = setTimeout(() => {
            previewMail({ templateId, recipient: previewRecipient, variables })
                .then((p) => req === previewReq.current && setPreview(p))
                .catch(() => req === previewReq.current && setPreview(null))
                .finally(() => req === previewReq.current && setPreviewLoading(false));
        }, 300);
        return () => clearTimeout(t);
    }, [templateId, previewRecipient, variables]);

    // What each template already sent to these recipients — our own history, not Resend's.
    useEffect(() => {
        if (!recipients.length) return setHistory({});
        getSentHistory(recipients).then(setHistory).catch(() => setHistory({}));
    }, [recipients]);

    useEffect(() => {
        if (!templateId || !recipients.length) return setCheck(null);
        checkRecipients(templateId, recipients).then(setCheck).catch(() => setCheck(null));
    }, [templateId, recipients]);

    const removeRecipient = (key: string) => setRecipients((prev) => prev.filter((r) => recipientKey(r) !== key));

    const doSend = async (force = false) => {
        if (!template) return;
        setSending(true);
        setNotice(null);
        const res = await sendMail({ templateId, recipients, variables, replyTo: replyTo.trim() || undefined, force, scheduledAt: later ? `${date}T${time}` : undefined });
        setSending(false);
        if (res.repeats) return setConfirmRepeats(res.repeats);
        setConfirmRepeats(null);
        if (res.error && !res.sent) return setNotice({ tone: "danger", text: res.error });
        setResult({ sent: res.sent ?? 0, failed: res.failed ?? 0, skipped: res.skipped ?? [], scheduledFor: res.scheduledFor });
        onSent();
    };

    const doTest = async () => {
        setNotice(null);
        const res = await sendTestMail({ templateId, recipient: previewRecipient, variables });
        setNotice(res.error ? { tone: "danger", text: res.error } : { tone: "success", text: `Test envoyé à ${res.to}` });
    };

    const willSend = check ? check.sendable : recipients.length;
    const canSend = !!template && recipients.length > 0 && missing.length === 0 && willSend > 0 && !sending && status?.resend.ok && (!later || (!!date && !!time));
    const plural = willSend > 1 ? "s" : "";

    if (result) {
        return (
            <Modal title={result.scheduledFor ? "Envoi programmé" : "Envoi terminé"} onClose={onClose} footer={<Button onClick={onClose}>Fermer</Button>}>
                <div className="space-y-4 p-6">
                    <p className="text-base text-ink">
                        <strong>{result.sent}</strong> email{result.sent > 1 ? "s" : ""} « {template?.name} » {result.scheduledFor ? "programmé" : "envoyé"}
                        {result.sent > 1 ? "s" : ""}
                        {result.scheduledFor && <> pour le {formatDate(result.scheduledFor, true)} (heure de Paris)</>}.
                    </p>
                    {result.scheduledFor && <p className="text-sm text-ink-muted">Il reste annulable jusqu'au départ, depuis « Envois programmés » en haut de la page.</p>}
                    {result.failed > 0 && <p className="text-sm text-danger">{result.failed} n'ont pas pu partir : voir l'historique de chaque destinataire.</p>}
                    {result.skipped.length > 0 && (
                        <div>
                            <p className="mb-2 text-sm text-ink-muted">Non envoyés :</p>
                            <ul className="space-y-1 text-sm text-ink">
                                {result.skipped.map((s) => (
                                    <li key={skipKey(s)}>
                                        {s.name || s.email || s.id} <span className="text-ink-muted">— {SKIP_REASON[s.reason]}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            wide
            title="Envoyer un email"
            subtitle={from ? `Expéditeur : ${from}` : undefined}
            onClose={onClose}
            footer={
                <>
                    {/* « Quand » lives next to the send button so it can't be missed below the fold. */}
                    <div className="mr-auto flex flex-wrap items-end gap-3">
                        <Segmented
                            value={later ? "later" : "now"}
                            onChange={(v) => setLater(v === "later")}
                            options={[
                                { value: "now", label: "Envoyer maintenant" },
                                { value: "later", label: <span className="inline-flex items-center gap-1"><CalendarClock className="h-4 w-4" />Programmer</span> },
                            ]}
                        />
                        {later && <When date={date} time={time} onDate={setDate} onTime={setTime} maxDays={status?.scheduleMaxDays ?? 30} />}
                        {notice && <p className={`text-sm ${notice.tone === "danger" ? "text-danger" : "text-success"}`}>{notice.text}</p>}
                    </div>
                    <Button variant="secondary" size="sm" onClick={doTest} disabled={!template || !status?.resend.ok}>
                        M'envoyer un test
                    </Button>
                    <Button variant="accent" onClick={() => doSend(false)} disabled={!canSend}>
                        {later ? <CalendarClock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                        {sending ? (later ? "Programmation…" : "Envoi…") : later ? `Programmer pour ${willSend} destinataire${plural}` : `Envoyer à ${willSend} destinataire${plural}`}
                    </Button>
                </>
            }
        >
            <div className="grid h-full min-h-0 grid-cols-1 lg:grid-cols-5">
                <div className="space-y-6 overflow-y-auto border-border p-6 lg:col-span-2 lg:border-r">
                    <Step n={1} title={`Destinataires (${recipients.length})`}>
                        <RecipientSearch exclude={keys} onAdd={(r) => setRecipients((prev) => (prev.some((p) => recipientKey(p) === recipientKey(r)) ? prev : [...prev, r]))} />
                        {recipients.length > 0 && (
                            <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
                                {recipients.map((r) => (
                                    <span key={recipientKey(r)} className="inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-surface-sunken py-1 pl-3 pr-1 text-xs text-ink">
                                        <span className="truncate">{r.name || r.email}</span>
                                        {r.kind === "contact" && <span className="text-ink-faint">· prospect</span>}
                                        <button onClick={() => removeRecipient(recipientKey(r))} aria-label={`Retirer ${r.email}`} className="rounded-full p-0.5 text-ink-faint hover:bg-surface hover:text-ink">
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        {recipients.length > 1 && (
                            <button onClick={() => setRecipients([])} className="text-xs text-ink-muted underline hover:text-ink">
                                Tout retirer
                            </button>
                        )}
                    </Step>

                    <Step n={2} title="Modèle">
                        {templates.length === 0 ? (
                            <p className="text-sm text-ink-muted">Aucun modèle publié dans Resend.</p>
                        ) : (
                            <div className="space-y-1.5">
                                {templates.map((t) => (
                                    <button
                                        key={t._id}
                                        type="button"
                                        onClick={() => setTemplateId(t._id)}
                                        className={`w-full rounded-lg border px-3 py-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                            templateId === t._id ? "border-accent bg-accent-subtle" : "border-border hover:bg-surface-sunken"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="flex min-w-0 items-center gap-2 text-sm font-medium text-ink">
                                                <span className="truncate">{t.name}</span>
                                                {t.resend?.hasUnpublished && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning" aria-label="Modifications non publiées" />}
                                            </p>
                                            <CategoryBadge category={t.category} />
                                        </div>
                                        <p className="truncate text-xs text-ink-muted">{t.subject}</p>
                                        <AlreadySent rows={history[t._id]} total={recipients.length} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </Step>

                    {template?.resend?.hasUnpublished && <UnpublishedNotice id={template.resend.id} />}

                    {template && (
                        <Step n={3} title="Personnaliser">
                            {template.freeVariables.map((k) => (
                                <div key={k}>
                                    <label className={fieldLabel}>
                                        <span className="font-mono text-xs">{k}</span>
                                    </label>
                                    <textarea
                                        rows={2}
                                        value={variables[k] ?? ""}
                                        onChange={(e) => setVariables((v) => ({ ...v, [k]: e.target.value }))}
                                        className={textareaClass}
                                        placeholder="Même texte pour tous les destinataires"
                                    />
                                </div>
                            ))}
                            <div>
                                <label className={fieldLabel}>Les réponses arrivent à</label>
                                <Input
                                    value={replyTo}
                                    onChange={(e) => setReplyTo(e.target.value)}
                                    placeholder={template.resend?.replyTo ? `${template.resend.replyTo} (défini dans Resend)` : "Votre adresse de connexion (par défaut)"}
                                />
                            </div>
                        </Step>
                    )}

                    {check && (check.skipped.length > 0 || check.repeats.length > 0) && (
                        <div className="space-y-2 rounded-2xl border border-warning/20 bg-warning-subtle p-4 text-sm text-ink">
                            <p className="flex items-center gap-2 font-medium text-warning">
                                <AlertTriangle className="h-4 w-4" />À vérifier avant d'envoyer
                            </p>
                            {check.skipped.length > 0 && (
                                <p>
                                    {check.skipped.length} ne recevront pas l'email : {check.skipped.map((s) => `${s.name || s.email || "?"} (${SKIP_REASON[s.reason]})`).join(", ")}.
                                </p>
                            )}
                            {check.repeats.length > 0 && (
                                <p>
                                    {check.repeats.length} ont déjà reçu ce modèle ces {status?.repeatWindowDays ?? 7} derniers jours :{" "}
                                    {check.repeats.map((s) => `${s.name || s.email} (${formatDate(s.lastSentAt)})`).join(", ")}.
                                </p>
                            )}
                        </div>
                    )}

                    {confirmRepeats && (
                        <div className="space-y-3 rounded-2xl border border-border-strong p-4">
                            <p className="text-sm text-ink">
                                {confirmRepeats.length} destinataire{confirmRepeats.length > 1 ? "s ont" : " a"} déjà reçu (ou va recevoir) cet email. L'envoyer quand même ?
                            </p>
                            <div className="flex gap-2">
                                <Button size="sm" onClick={() => doSend(true)} disabled={sending}>
                                    Envoyer quand même
                                </Button>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => {
                                        const drop = new Set(confirmRepeats.map(skipKey));
                                        setRecipients((prev) => prev.filter((r) => !drop.has(recipientKey(r))));
                                        setConfirmRepeats(null);
                                    }}
                                >
                                    Les retirer
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex min-h-0 flex-col gap-3 bg-canvas p-6 lg:col-span-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-1 text-sm text-ink-muted">
                            {recipients.length > 1 && (
                                <button onClick={() => setPreviewIdx((i) => Math.max(0, i - 1))} disabled={previewIdx === 0} aria-label="Destinataire précédent" className="rounded-lg p-1 hover:bg-surface-sunken disabled:opacity-40">
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                            )}
                            <span className="truncate">
                                Aperçu pour <strong className="text-ink">{previewRecipient ? previewRecipient.name || previewRecipient.email : "un exemple"}</strong>
                                {recipients.length > 1 && ` (${Math.min(previewIdx, recipients.length - 1) + 1}/${recipients.length})`}
                            </span>
                            {recipients.length > 1 && (
                                <button
                                    onClick={() => setPreviewIdx((i) => Math.min(recipients.length - 1, i + 1))}
                                    disabled={previewIdx >= recipients.length - 1}
                                    aria-label="Destinataire suivant"
                                    className="rounded-lg p-1 hover:bg-surface-sunken disabled:opacity-40"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <Segmented
                            value={mobile ? "mobile" : "desktop"}
                            onChange={(v) => setMobile(v === "mobile")}
                            options={[
                                { value: "desktop", label: <Monitor className="h-4 w-4" aria-label="Ordinateur" /> },
                                { value: "mobile", label: <Smartphone className="h-4 w-4" aria-label="Mobile" /> },
                            ]}
                        />
                    </div>
                    {missing.length > 0 && preview && (
                        <Badge tone="warning" className="self-start">
                            À remplir : {missing.join(", ")}
                        </Badge>
                    )}
                    <MailFrame html={preview?.html} subject={preview?.subject} from={from} mobile={mobile} loading={previewLoading} />
                </div>
            </div>
        </Modal>
    );
}
